import '../pages/contact.css';
import { NotificationsButton } from './Notifications';
import { FriendsButton } from './Friends';
import { Gift, Shield } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { IslandFooter } from './IslandFooter';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Heart,
  History,
  LogOut,
  Crown,
  ChevronDown,
  EyeOff,
  Settings2,
  Search,
} from 'lucide-react';
import { usePlatform } from '../store';
import { api, json } from '../api';
import { ThemeToggle } from './ThemeToggle';
import { usePrivacy } from './PrivacyMode';
import { shortcutLabel } from '../privacy';
import { BrandLogo } from './BrandMark';
import { BRAND } from '../../shared/brand';
import { WorkdayButton } from './WorkdayCountdown';
import './site-layout.css';
import { useTheme } from '../theme';
import '../pages/island-atlas.css';
import './atlas-pages.css';
import '../pages/island-community.css';
import '../pages/island-rankings.css';
import './atlas-quality.css';
import { AtlasMark } from './IslandMap';

export function Layout() {
  const { data, refresh, notify } = usePlatform();
  const { settings, activate, configure } = usePrivacy();
  const location = useLocation();
  const navigate = useNavigate();
  const { pageStyle } = useTheme();
  const atlas =
    (pageStyle === 'island' || location.pathname === '/wishes') &&
    !location.pathname.startsWith('/admin');
  const [search, setSearch] = useState('');
  useEffect(() => {
    setSearch(new URLSearchParams(location.search).get('q') || '');
  }, [location.search]);
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const account = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const name = data?.user?.nickname || data?.user?.username || '访客';
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!account.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  return (
    <div className={`island-site${atlas ? ' atlas-site' : ''}`}>
      <a className="site-skip" href="#site-content">
        跳转到内容
      </a>
      <header className="island-header">
        <div className="island-header-inner">
          <Link
            to="/"
            className="island-brand"
            aria-label={`${data?.settings.siteName || BRAND.name}首页`}
          >
            {atlas ? (
              <>
                <span className="brand-icon">
                  <AtlasMark />
                </span>
                <span className="atlas-wordmark">MOYU ISLAND</span>
              </>
            ) : (
              <BrandLogo />
            )}
          </Link>
          <nav className="island-tabs" aria-label="主导航">
            <NavLink to="/" end>
              发现好玩
            </NavLink>
            <NavLink to="/library">游戏库</NavLink>
            <NavLink to="/rankings">排行榜</NavLink>
            <NavLink to="/community">岛民广场</NavLink>
            {pageStyle === 'island' && <NavLink to="/wishes">心愿榜</NavLink>}
            {data?.settings.membershipPageEnabled === true && (
              <NavLink to="/membership" className="island-membership-link">
                会员<span className="island-membership-badge">免费</span>
              </NavLink>
            )}
          </nav>
          {atlas && (
            <form
              className="atlas-header-search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                navigate(
                  '/library' + (search.trim() ? '?q=' + encodeURIComponent(search.trim()) : ''),
                );
              }}
            >
              <input
                aria-label="搜索全岛游戏"
                placeholder="搜索游戏，发现你的下一局"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <button type="submit" aria-label="提交游戏搜索">
                <Search size={16} />
              </button>
            </form>
          )}
          <div
            className="island-account"
            ref={account}
            onBlur={(event) => {
              if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node))
                setOpen(false);
            }}
          >
            <FriendsButton />
            <NotificationsButton />
            <ThemeToggle mode="combined" showHint={location.pathname === '/' && !!data} />
            {!data?.user && (
              <Link className="island-sign-in" to="/login">
                登录 / 注册
              </Link>
            )}
            <button
              className={`island-account-trigger${!data?.user ? ' is-guest' : ''}${open ? ' is-open' : ''}`}
              ref={trigger}
              aria-expanded={open}
              aria-controls="personal-panel"
              aria-label={data?.user ? '个人菜单' : '快捷设置'}
              onClick={() => setOpen((value) => !value)}
            >
              {data?.user ? (
                <>
                  <UserAvatar src={data.user.avatarUrl} />
                  <span className="island-account-name">{name}</span>
                  <ChevronDown size={15} className="account-chevron" />
                </>
              ) : (
                <Settings2 size={18} />
              )}
            </button>
            {open && (
              <div className="island-personal-panel" id="personal-panel">
                <div className="personal-group">
                  {data?.user && data.settings.referralEnabled && (
                    <Link to="/invite">
                      <Gift size={18} />
                      邀请好友
                    </Link>
                  )}
                  <Link to="/favorites">
                    <Heart size={18} />
                    我的收藏
                    {!!data?.favorites.length && (
                      <span className="personal-count">{data.favorites.length}</span>
                    )}
                  </Link>
                  <Link to="/recent">
                    <History size={18} />
                    最近游玩
                  </Link>
                  {data?.settings.membershipPageEnabled === true && (
                    <Link to="/membership">
                      <Crown size={18} />
                      会员中心
                    </Link>
                  )}
                </div>
                <div className="personal-group">
                  <WorkdayButton label onOpen={() => setOpen(false)} />
                  <button
                    onClick={() => {
                      setOpen(false);
                      configure();
                    }}
                  >
                    <Settings2 size={18} />
                    老板键设置
                  </button>
                  <button
                    onClick={() => {
                      setOpen(false);
                      activate();
                    }}
                  >
                    <EyeOff size={18} />
                    隐藏当前页面<kbd>{shortcutLabel(settings.shortcut)}</kbd>
                  </button>
                </div>
                <div className="personal-group">
                  <Link to="/contact" onClick={() => setOpen(false)}>
                    联系我们 / 意见反馈
                  </Link>
                </div>
                {data?.user && (
                  <div className="personal-group">
                    {(data.user.role === 'admin' || data.user.role === 'superadmin') && (
                      <Link to="/admin" onClick={() => setOpen(false)}>
                        <Shield size={18} />
                        管理后台
                      </Link>
                    )}
                    <Link to="/account/profile">
                      <Settings2 size={18} />
                      个人资料
                    </Link>
                    <button
                      className="personal-logout"
                      disabled={loggingOut}
                      onClick={async () => {
                        setLoggingOut(true);
                        try {
                          await api('/auth/logout', json('POST', {}));
                          await refresh();
                          setOpen(false);
                          notify('已退出登录');
                        } catch {
                          notify('退出失败，请稍后重试');
                        } finally {
                          setLoggingOut(false);
                        }
                      }}
                    >
                      <LogOut size={18} />
                      {loggingOut ? '正在退出…' : '退出登录'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
      <main id="site-content" className="main-content island-content" tabIndex={-1}>
        <Outlet />
      </main>
      <div className="contact-footer-entry">
        <Link to="/contact">联系我们 · 意见反馈</Link>
      </div>
      {!atlas && location.pathname !== '/membership' && <IslandFooter />}
    </div>
  );
}
